import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import PartCard from '../components/PartCard';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

function Browse() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBin, setSelectedBin] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bin') || 'All';
  });

  useEffect(() => {
    fetch(`${API_BASE}/api/inventories`)
      .then(res => res.json())
      .then(data => {
        setInventory(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '1.8rem', marginBottom: '12px', color: colors.navy }}>⟳</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
          Loading inventory from BrickLink...
        </div>
      </div>
    );
  }

  const bins = ['All', ...new Set(inventory.map(l => l.remarks || '(no remarks)'))].sort();

  let filtered = [...inventory];
  if (selectedBin !== 'All') {
    filtered = filtered.filter(l => (l.remarks || '(no remarks)') === selectedBin);
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(l =>
      (l.item?.no || '').toLowerCase().includes(q) ||
      (l.item?.name || '').toLowerCase().includes(q)
    );
  }

  const grouped = {};
  filtered.forEach(lot => {
    const bin = lot.remarks || '(no remarks)';
    if (!grouped[bin]) grouped[bin] = [];
    grouped[bin].push(lot);
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Browse Inventory" icon="⊞" />

      <div style={{
        fontSize: '0.85rem',
        color: colors.greenDark,
        marginBottom: '16px',
        fontWeight: 700,
      }}>
        {inventory.length} lots loaded from BrickLink
      </div>

      <select
        value={selectedBin}
        onChange={(e) => setSelectedBin(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          color: colors.textPrimary,
          fontFamily: "'Outfit', sans-serif",
          fontSize: '0.9rem',
          outline: 'none',
          marginBottom: '10px',
        }}
      >
        {bins.map(bin => (
          <option key={bin} value={bin}>{bin === 'All' ? 'Jump to bin...' : bin}</option>
        ))}
      </select>

      <input
        type="text"
        placeholder="Search part # or name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          color: colors.textPrimary,
          fontFamily: "'Outfit', sans-serif",
          fontSize: '0.9rem',
          outline: 'none',
          marginBottom: '20px',
        }}
      />

      <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginBottom: '16px' }}>
        Showing {filtered.length} lots
      </div>

      {Object.entries(grouped).sort().map(([bin, lots]) => (
        <div key={bin}>
          <div style={{
            borderLeft: `3px solid ${colors.greenDark}`,
            borderRadius: '6px',
            padding: '12px 20px',
            margin: '24px 0 12px 0',
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderLeftWidth: '3px',
            borderLeftColor: colors.greenDark,
          }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: colors.navy }}>▦ {bin}</div>
            <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginTop: '3px' }}>{lots.length} lots</div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '10px',
          }}>
            {[...lots].sort((a, b) => (a.item?.no || '').localeCompare(b.item?.no || '') || (a.quantity || 0) - (b.quantity || 0)).map(lot => (
              <PartCard key={lot.inventory_id} lot={lot} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default Browse;